import { useEffect, useRef, useState } from "react";
import {
  Paper,
  Typography,
  IconButton,
  Box,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const COLS = 10;
const ROWS = 20;
const BLOCK = 20;

const SHAPES = [
  [[1, 1, 1, 1]],
  [
    [1, 1],
    [1, 1],
  ],
  [
    [0, 1, 0],
    [1, 1, 1],
  ],
  [
    [1, 0, 0],
    [1, 1, 1],
  ],
  [
    [0, 0, 1],
    [1, 1, 1],
  ],
  [
    [1, 1, 0],
    [0, 1, 1],
  ],
  [
    [0, 1, 1],
    [1, 1, 0],
  ],
];
const COLORS = [
  "#ff0000ff",
  "#ff8800ff",
  "#ffee00ff",
  "#06bd00ff",
  "#00daaaff",
  "#0099ffff",
  "#a103ebff",
];

function randomPiece() {
  const i = Math.floor(Math.random() * SHAPES.length);
  return { shape: SHAPES[i], color: COLORS[i], x: 3, y: 0 };
}

export default function Tetris() {
  const canvasRef = useRef(null);
  const [board, setBoard] = useState(
    Array.from({ length: ROWS }, () => Array(COLS).fill(null))
  );
  const [piece, setPiece] = useState(randomPiece());
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const [linesCleared, setLinesCleared] = useState(0);
  const [level, setLevel] = useState(1);
  const [dropSpeed, setDropSpeed] = useState(500);

  const [highScores, setHighScores] = useState(() => {
    const saved = localStorage.getItem("tetris-highscores");
    return saved ? JSON.parse(saved) : [];
  });

  const [paused, setPaused] = useState(false);

  // Keyboard controls
  useEffect(() => {
    const handler = (e) => {
      if (gameOver || paused) return;
      if (["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        if (e.key === "ArrowLeft") move(-1);
        if (e.key === "ArrowRight") move(1);
        if (e.key === "ArrowDown") drop();
        if (e.key === "ArrowUp") rotate();
      }
    };
    window.addEventListener("keydown", handler, { passive: false });
    return () => window.removeEventListener("keydown", handler);
  });

  // Main game loop — respects pause
  useEffect(() => {
    if (gameOver || paused) return;
    const interval = setInterval(drop, dropSpeed);
    return () => clearInterval(interval);
  }, [dropSpeed, gameOver, paused, piece]);

  function collide(nextPiece) {
    const { shape, x, y } = nextPiece;
    for (let dy = 0; dy < shape.length; dy++) {
      for (let dx = 0; dx < shape[dy].length; dx++) {
        if (shape[dy][dx]) {
          const newY = y + dy;
          const newX = x + dx;
          if (
            newY >= ROWS ||
            newX < 0 ||
            newX >= COLS ||
            (newY >= 0 && board[newY][newX])
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  function merge() {
    const newBoard = board.map((r) => r.slice());
    piece.shape.forEach((row, dy) =>
      row.forEach((cell, dx) => {
        if (cell && piece.y + dy >= 0)
          newBoard[piece.y + dy][piece.x + dx] = piece.color;
      })
    );
    const cleared = newBoard.filter((r) => r.some((c) => !c));
    const lines = ROWS - cleared.length;
    if (lines > 0) {
      setScore((s) => s + lines * 100);
      setLinesCleared((prev) => prev + lines);
    }
    while (cleared.length < ROWS) cleared.unshift(Array(COLS).fill(null));
    setBoard(cleared);
  }

  // Level progression
  useEffect(() => {
    const newLevel = Math.floor(linesCleared / 10) + 1;
    if (newLevel !== level) {
      setLevel(newLevel);
      setDropSpeed(Math.max(500 - (newLevel - 1) * 40, 100));
    }
  }, [linesCleared, level]);

  function drop() {
    const next = { ...piece, y: piece.y + 1 };
    if (collide(next)) {
      merge();
      const newPiece = randomPiece();
      if (collide(newPiece)) {
        const updated = [...highScores, score]
          .sort((a, b) => b - a)
          .slice(0, 5);
        setHighScores(updated);
        localStorage.setItem("tetris-highscores", JSON.stringify(updated));
        setGameOver(true);
        return;
      }
      setPiece(newPiece);
    } else {
      setPiece(next);
    }
  }

  function move(dir) {
    const next = { ...piece, x: piece.x + dir };
    if (!collide(next)) setPiece(next);
  }

  function rotate() {
    const rotated = piece.shape[0].map((_, i) =>
      piece.shape.map((row) => row[i]).reverse()
    );
    const next = { ...piece, shape: rotated };
    if (!collide(next)) setPiece(next);
  }

  function reset() {
    setBoard(Array.from({ length: ROWS }, () => Array(COLS).fill(null)));
    setScore(0);
    setPiece(randomPiece());
    setGameOver(false);
    setLinesCleared(0);
    setLevel(1);
    setDropSpeed(500);
    setPaused(false);
  }

  function togglePause() {
    if (gameOver) return;
    setPaused((p) => !p);
  }

  // Draw the game
  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, COLS * BLOCK, ROWS * BLOCK);

    board.forEach((row, y) =>
      row.forEach((color, x) => {
        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(x * BLOCK, y * BLOCK, BLOCK, BLOCK);
        }
      })
    );

    piece.shape.forEach((row, dy) =>
      row.forEach((cell, dx) => {
        if (cell) {
          ctx.fillStyle = piece.color;
          ctx.fillRect(
            (piece.x + dx) * BLOCK,
            (piece.y + dy) * BLOCK,
            BLOCK,
            BLOCK
          );
        }
      })
    );
  }, [board, piece]);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      {/* Left: Game */}
      <Paper sx={{ p: 2, textAlign: "center", position: "relative" }}>
        {/* ✅ IconButton Pause/Play top-left */}
        <IconButton
          onClick={togglePause}
          sx={{ position: "absolute", top: 8, left: 8, color: "white" }}
        >
          {paused ? <PlayArrowIcon /> : <PauseIcon />}
        </IconButton>

        <Typography variant="h6" sx={{ mt: 1 }}>
          Mini Tetris
        </Typography>

        <canvas
          ref={canvasRef}
          width={COLS * BLOCK}
          height={ROWS * BLOCK}
          style={{ border: "1px solid #111", background: "#111" }}
        />

        <Typography variant="body2">Score: {score}</Typography>
        <Typography variant="body2">Level: {level}</Typography>
        <Typography variant="body2">Lines: {linesCleared}</Typography>

        {gameOver && (
          <>
            <Typography color="error" variant="body2">
              Game Over
            </Typography>
            <IconButton onClick={reset} color="primary">
              <PlayArrowIcon />
            </IconButton>
          </>
        )}

        {paused && !gameOver && (
          <Typography variant="body2" color="info.main">
            Paused
          </Typography>
        )}

        <Typography variant="caption" display="block">
          Use arrow keys: ← → move, ↓ drop, ↑ rotate
        </Typography>
      </Paper>

      {/* Right: High Scores */}
      <Paper sx={{ p: 2, minWidth: 150 }}>
        <Typography variant="h6" gutterBottom>
          Top 5 Scores
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <List dense>
          {highScores.length === 0 && <ListItem>No scores yet</ListItem>}
          {highScores.map((s, i) => (
            <ListItem key={i}>
              {i + 1}. {s}
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
}
